import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ModerationNav } from '../components/ModerationNav';
import { fetchAudit, type AuditEntry, type MeResponse } from '../lib/api';

function label(value: string): string {
  return value.replace(/_/g, ' ');
}

/**
 * Append-only audit log with drill-through. Each row can be expanded
 * to show the metadata blob and links into the underlying event or
 * user, so a moderator skimming the log can land on the full context
 * in one click. Most-recent first.
 */
export function AuditLog({ me }: { me: MeResponse }) {
  const communityId = me.memberships[0]?.communityId ?? '';
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!communityId) return;
    fetchAudit(communityId)
      .then(setEntries)
      .catch(() => setError('Could not load the audit log.'));
  }, [communityId]);

  return (
    <main className="wide">
      <div className="card">
        <ModerationNav />
        <h1>Audit log</h1>
        <p className="muted">
          Append-only record of every flag and moderator action. Click a row to expand its metadata
          and jump to the related event or user.
        </p>
        {error && <p className="error">{error}</p>}
        {!entries && !error && <p>Loading…</p>}
        {entries && entries.length === 0 && <p className="muted">No entries yet.</p>}
        {entries && entries.length > 0 && (
          <ul className="audit-list">
            {entries.map((entry) => {
              const isOpen = expanded === entry.id;
              const userTarget = entry.targetType === 'user' ? entry.targetId : null;
              return (
                <li key={entry.id} className="audit-row">
                  <button
                    type="button"
                    className="audit-row-toggle"
                    onClick={() => setExpanded(isOpen ? null : entry.id)}
                    aria-expanded={isOpen}
                  >
                    <span className="audit-head">
                      <strong>{label(entry.action)}</strong>
                      <span className="muted">
                        {entry.targetType}:{entry.targetId.slice(0, 8)}
                      </span>
                      <span className="muted">{entry.actorUserId ? 'moderator' : 'system'}</span>
                      <span className="muted audit-when">
                        {new Date(entry.createdAt).toLocaleString()}
                      </span>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="audit-detail">
                      {entry.metadata !== null && entry.metadata !== undefined && (
                        <pre className="audit-meta">{JSON.stringify(entry.metadata, null, 2)}</pre>
                      )}
                      <div className="audit-links">
                        {entry.eventId && (
                          <Link to={`/moderation/events/${entry.eventId}`}>
                            Open the moderation event →
                          </Link>
                        )}
                        {userTarget && (
                          <Link to={`/moderation/users/${userTarget}`}>
                            See this user's full history →
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
}
